(() => {
  if (customElements.get('somdul-wholesale-preview')) return;

  class SomdulWholesaleForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.currentStep = 1;
      this.sending = false;
      this.copy = {};
      this.querySelectorAll('[data-copy-key]').forEach((item) => { this.copy[item.dataset.copyKey] = item.textContent.trim(); });
      this.form = this.querySelector('form');
      const controls = this.querySelector('[data-controls]');
      controls.disabled = false;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => {
        if (event.target.name) this.clearError(event.target.name);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        if (event.target.matches('input:not([type="radio"]):not([type="checkbox"])')) {
          event.preventDefault();
          const panel = this.querySelector('[data-panel="' + this.currentStep + '"]');
          const button = panel && panel.querySelector('[data-next], [data-review], [data-finish]');
          if (button) button.click();
        }
      });
      this.addEventListener('submit', (event) => { if (!this.sending) event.preventDefault(); });
      this.syncOther();
      const params = new URLSearchParams(window.location.search);
      if (controls.dataset.posted === 'true' || params.get('contact_posted') === 'true') {
        this.showStep(5, false);
      }
    }

    text(key) { return this.copy[key] || ''; }
    control(name) { return this.querySelector('[name="' + name + '"]'); }
    value(name) { const control = this.control(name); return control ? control.value.trim() : ''; }
    checked(name) { return this.querySelector('[name="' + name + '"]:checked'); }

    clearError(name) {
      const error = this.querySelector('[data-error="' + name + '"]');
      if (error) { error.textContent = ''; error.hidden = true; }
      this.querySelectorAll('[name="' + name + '"]').forEach((input) => input.removeAttribute('aria-invalid'));
    }

    setError(name, message) {
      const error = this.querySelector('[data-error="' + name + '"]');
      if (error) { error.textContent = this.text(message); error.hidden = false; }
      this.querySelectorAll('[name="' + name + '"]').forEach((input) => input.setAttribute('aria-invalid', 'true'));
    }

    validate(step) {
      const panel = this.querySelector('[data-panel="' + step + '"]');
      panel.querySelectorAll('[data-error]').forEach((error) => this.clearError(error.dataset.error));
      const processed = new Set();
      panel.querySelectorAll('input[required], select[required]').forEach((input) => {
        if (input.disabled || processed.has(input.name)) return;
        processed.add(input.name);
        if (input.type === 'radio') {
          if (!this.checked(input.name)) this.setError(input.name, 'error_choice');
        } else if (input.type === 'checkbox') {
          if (!input.checked) this.setError(input.name, 'error_confirm');
        } else if (!input.value.trim()) {
          this.setError(input.name, 'error_required');
        } else if (input.type === 'email' && !input.validity.valid) {
          this.setError(input.name, 'error_email');
        } else if (input.type === 'tel') {
          const digits = input.value.replace(/\D/g, '');
          if (!/^\+?[\d\s().-]+$/.test(input.value) || digits.length < 10 || digits.length > 15) this.setError(input.name, 'error_mobile');
        } else if (input.name === 'zip') {
          if (!/^\d{5}(-\d{4})?$/.test(input.value.trim())) this.setError(input.name, 'error_zip');
        } else if (input.name === 'proof_link') {
          const link = input.value.trim();
          if (!(/^@[A-Za-z0-9._]{2,}$/.test(link) || (/\./.test(link) && !/\s/.test(link) && link.length > 3))) this.setError(input.name, 'error_link');
        }
      });
      if (step === 2 && !this.checked('ingredients')) this.setError('ingredients', 'error_ingredients');
      const first = panel.querySelector('[aria-invalid="true"]');
      if (first) {
        const alert = this.querySelector('[data-alert]');
        alert.textContent = this.text('error_summary');
        alert.hidden = false;
        if (this.currentStep !== step) this.showStep(step, false);
        first.focus();
        return false;
      }
      return true;
    }

    showStep(step, focus = true) {
      this.currentStep = step;
      this.querySelectorAll('[data-panel]').forEach((panel) => { panel.hidden = Number(panel.dataset.panel) !== step; });
      this.querySelector('[data-back]').hidden = step === 1 || step === 5;
      this.querySelector('[data-back-home]').hidden = step !== 1;
      const progressKey = step < 4 ? 'step_' + step : step === 4 ? 'review_progress' : 'complete_progress';
      this.querySelector('[data-progress-label]').textContent = this.text(progressKey);
      this.querySelectorAll('.sw-progress li').forEach((item, index) => { item.classList.toggle('is-current', index < Math.min(step, 3)); });
      if (focus) {
        const alert = this.querySelector('[data-alert]');
        alert.textContent = '';
        alert.hidden = true;
        this.querySelector('[data-panel="' + step + '"] h1').focus();
      }
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-next')) {
        if (this.validate(this.currentStep)) this.showStep(Number(button.dataset.next));
      } else if (button.hasAttribute('data-back')) {
        this.showStep(Math.max(1, this.currentStep - 1));
      } else if (button.hasAttribute('data-review')) {
        for (const step of [1, 2, 3]) { if (!this.validate(step)) return; }
        this.buildSummary();
        this.showStep(4);
      } else if (button.hasAttribute('data-edit')) {
        this.showStep(Number(button.dataset.edit));
      } else if (button.hasAttribute('data-finish')) {
        this.submitEnquiry(button);
      }
    }

    onChange(event) {
      const input = event.target;
      if (input.name) this.clearError(input.name);
      if (input.name === 'ingredients') this.syncOther();
    }

    syncOther() {
      const otherInput = this.querySelector('[name="ingredients"][value="other"]');
      const other = otherInput ? otherInput.checked : false;
      this.querySelector('[data-other-ingredients]').hidden = !other;
      this.control('other_ingredients').disabled = !other;
      if (!other) this.clearError('other_ingredients');
    }

    optionText(name) {
      const selected = this.checked(name);
      return selected ? selected.nextElementSibling.textContent.trim() : this.text('not_provided');
    }

    ingredientLabels() {
      return Array.from(this.querySelectorAll('[name="ingredients"]:checked')).map((input) => input.value === 'other' ? this.value('other_ingredients') : input.nextElementSibling.textContent.trim()).filter(Boolean);
    }

    buildSummary() {
      const confirmInput = this.control('confirm');
      const groups = {
        1: [['restaurant_name', this.value('restaurant')], ['address', this.value('address')], ['city', this.value('city')], ['state', this.value('state')], ['zip', this.value('zip')], ['business_phone', this.value('business_phone')], ['restaurant_type', this.optionText('restaurant_type')], ['locations', this.optionText('locations')], ['proof_link', this.value('proof_link')], ['supplier', this.value('supplier')]],
        2: [['ingredients', this.ingredientLabels().join(', ')], ['monthly_spend', this.optionText('spend')], ['owner', this.value('owner')], ['mobile', this.value('mobile')], ['email', this.value('email')], ['line', this.value('line')]],
        3: [['permit_number', this.value('permit_number')], ['health_permit', this.value('health_permit')], ['referral', this.value('referral')], ['confirm', confirmInput && confirmInput.checked ? this.text('confirmed') : '']]
      };
      Object.entries(groups).forEach(([group, rows]) => {
        const list = this.querySelector('[data-summary="' + group + '"]');
        list.textContent = '';
        rows.forEach(([key, value]) => {
          const row = document.createElement('div');
          const label = document.createElement('dt');
          const detail = document.createElement('dd');
          label.textContent = this.text(key);
          detail.textContent = value || this.text('not_provided');
          row.append(label, detail);
          list.append(row);
        });
      });
    }

    setContactField(key, value) {
      const field = this.querySelector('[data-contact-field="' + key + '"]');
      if (field) field.value = value || '';
    }

    submitEnquiry(button) {
      if (this.sending) return;
      for (const step of [1, 2, 3]) { if (!this.validate(step)) return; }
      const restaurant = this.value('restaurant');
      const location = [this.value('address'), this.value('city'), [this.value('state'), this.value('zip')].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      const ingredients = this.ingredientLabels().join(', ');
      const fields = {
        subject: 'Wholesale enquiry - ' + restaurant,
        email: this.value('email'),
        name: this.value('owner'),
        phone: this.value('mobile'),
        restaurant: restaurant,
        location: location,
        business_phone: this.value('business_phone'),
        proof_link: this.value('proof_link'),
        restaurant_type: this.optionText('restaurant_type'),
        locations: this.optionText('locations'),
        current_supplier: this.value('supplier'),
        ingredients: ingredients,
        monthly_spend: this.optionText('spend'),
        line_id: this.value('line'),
        permit_number: this.value('permit_number'),
        health_permit: this.value('health_permit'),
        referral: this.value('referral')
      };
      const body = [
        'WHOLESALE ENQUIRY',
        'Restaurant: ' + restaurant,
        'Address: ' + location,
        'Restaurant phone: ' + fields.business_phone,
        'Listing / link: ' + fields.proof_link,
        'Type: ' + fields.restaurant_type,
        'Locations: ' + fields.locations,
        'Current supplier: ' + (fields.current_supplier || '-'),
        'Ingredients: ' + (ingredients || '-'),
        'Monthly spend: ' + fields.monthly_spend,
        'Contact: ' + fields.name + ' / ' + fields.phone + ' / ' + fields.email,
        'LINE: ' + (fields.line_id || '-'),
        'Seller permit / resale number: ' + (fields.permit_number || '-'),
        'Health permit: ' + (fields.health_permit || '-'),
        'Referral: ' + (fields.referral || '-'),
        'Owner confirmation: yes',
        'Language: ' + document.documentElement.lang,
        'VERIFY BEFORE PRICING: 1) permit number with state (CDTFA for CA) 2) listing matches address + phone 3) call the restaurant line'
      ].join('\n');
      Object.entries(fields).forEach(([key, value]) => this.setContactField(key, value));
      this.setContactField('body', body);
      if (!this.form) return;
      this.sending = true;
      if (button) button.disabled = true;
      this.form.submit();
    }
  }

  customElements.define('somdul-wholesale-preview', SomdulWholesaleForm);
})();
